import { useEffect, useState } from 'react';
import { Combobox, Dropdown, Option } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchLoteCycles, fetchLoteCropCycles } from '../../api/crop.api';
import { useT } from '../../i18n';

const USE_TYPED = '__use_typed__';
const normalize = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export interface CycleComboboxProps {
  /** Lote cuyos ciclos se ofrecen (vacío = solo texto libre). */
  loteId: string;
  /** Zona o subzona elegida: ofrece solo sus ciclos (si ya tiene) en vez de texto libre. */
  moduleId?: string;
  value: string;
  onChange: (ciclo: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Selector de ciclo/batchId con búsqueda (#57): ofrece los ciclos que el lote ya
 * conoce (fenología, riego, presupuestos, actividades) y filtra mientras se teclea.
 * Un ciclo nuevo sigue siendo válido (se crea al registrar), por eso se permite
 * "Usar «X»" — mismo patrón que la pantalla Cultivo (#354).
 */
export function CycleCombobox({ loteId, moduleId, value, onChange, disabled, placeholder }: CycleComboboxProps) {
  const t = useT();
  const { data: cycles = [] } = useQuery({
    queryKey: ['lote-cycles', loteId],
    queryFn: () => fetchLoteCycles(loteId),
    enabled: !!loteId,
  });
  const { data: cropCycles = [] } = useQuery({
    queryKey: ['lote-crop-cycles', loteId],
    queryFn: () => fetchLoteCropCycles(loteId),
    enabled: !!loteId && !!moduleId,
  });
  // Ciclos de la zona elegida, el abierto primero.
  const zoneCycles = moduleId
    ? cropCycles.filter((c) => c.targetId === moduleId)
        .sort((a, b) => (a.status === b.status ? b.startedAt.localeCompare(a.startedAt) : a.status === 'OPEN' ? -1 : 1))
    : [];
  const [query, setQuery] = useState(value);
  useEffect(() => { setQuery(value); }, [value]);

  const q = normalize(query);
  const options = cycles.filter((c) => normalize(c.ciclo).includes(q));
  const exact = q !== '' ? cycles.find((c) => normalize(c.ciclo) === q) : undefined;
  const showUseTyped = query.trim() !== '' && !exact;

  const confirm = (v: string) => { const next = v.trim(); setQuery(next); onChange(next); };

  if (moduleId && zoneCycles.length > 0) {
    return (
      <Dropdown
        disabled={disabled}
        value={value}
        selectedOptions={value ? [value] : []}
        placeholder={placeholder ?? t.crop.cicloPlaceholder}
        onOptionSelect={(_, d) => onChange(d.optionValue ?? '')}
      >
        {zoneCycles.map((c) => <Option key={c.ciclo} value={c.ciclo}>{c.ciclo}</Option>)}
      </Dropdown>
    );
  }

  return (
    <Combobox
      freeform
      disabled={disabled}
      value={query}
      selectedOptions={exact ? [exact.ciclo] : []}
      placeholder={placeholder ?? t.crop.cicloPlaceholder}
      onInput={(e) => {
        const v = (e.target as HTMLInputElement).value;
        setQuery(v);
        onChange(v);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter' && query.trim()) confirm(exact?.ciclo ?? query); }}
      onBlur={() => { if (query.trim() && query.trim() !== value) confirm(exact?.ciclo ?? query); }}
      onOptionSelect={(_, d) => d.optionValue !== undefined && confirm(d.optionValue === USE_TYPED ? query : d.optionValue ?? '')}
    >
      {options.map((c) => <Option key={c.ciclo} value={c.ciclo}>{c.ciclo}</Option>)}
      {showUseTyped && (
        <Option key={USE_TYPED} value={USE_TYPED} text={query}>
          {t.crop.cicloUse.replace('{value}', query.trim())}
        </Option>
      )}
    </Combobox>
  );
}
